import React, { useState, useMemo, useCallback, memo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Button,
  Alert,
  Skeleton,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  CompareArrows as CompareIcon,
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
} from '@mui/icons-material';

// Import existing chart components
import { RevenueChart } from '../Financial/Charts/RevenueChart';

// Types - using relative import to backend types
interface RevenueData {
  date: string;
  amount: number;
  propertyId: string;
  propertyName: string;
  source?: string;
  bookingCount?: number;
}

interface HostRevenueData {
  hostId: string;
  hostName: string;
  totalRevenue: number;
  propertyCount: number;
  averageRevenuePerProperty: number;
  monthlyData: RevenueData[];
  growthRate: number;
  status: 'active' | 'inactive';
}

interface SystemRevenueData {
  totalSystemRevenue: number;
  hostCount: number;
  propertyCount: number;
  averageRevenuePerHost: number;
  averageRevenuePerProperty: number;
  topPerformingHosts: HostRevenueData[];
  monthlyTrends: RevenueData[];
  hostComparison: HostRevenueData[];
  revenueDistribution: Array<{
    hostId: string;
    hostName: string;
    revenue: number;
    percentage: number;
  }>;
}

interface SystemRevenueAnalyticsProps {
  data?: SystemRevenueData;
  loading?: boolean;
  error?: string;
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  selectedHosts?: string[];
  onHostSelectionChange?: (hostIds: string[]) => void;
  onRefresh?: () => void;
}

interface ChartConfig {
  type: 'line' | 'bar' | 'area';
  groupBy: 'day' | 'week' | 'month';
  showComparison: boolean;
  showTrends: boolean;
}

const SystemRevenueAnalytics: React.FC<SystemRevenueAnalyticsProps> = memo(({
  data,
  loading = false,
  error,
  dateRange,
  selectedHosts = [],
  onHostSelectionChange,
  onRefresh,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Chart configuration state
  const [chartConfig, setChartConfig] = useState<ChartConfig>({
    type: 'line',
    groupBy: 'month',
    showComparison: true,
    showTrends: true,
  });

  const [viewMode, setViewMode] = useState<'overview' | 'comparison' | 'trends' | 'distribution'>('overview');

  // Mock data for development - replace with actual API data
  const mockData: SystemRevenueData = useMemo(() => ({
    totalSystemRevenue: 285000,
    hostCount: 12,
    propertyCount: 28,
    averageRevenuePerHost: 23750,
    averageRevenuePerProperty: 10179,
    topPerformingHosts: [
      {
        hostId: 'host-1',
        hostName: 'John Smith',
        totalRevenue: 65000,
        propertyCount: 4,
        averageRevenuePerProperty: 16250,
        monthlyData: [
          { date: '2024-01-01', amount: 12000, bookingCount: 8, propertyId: 'prop-1', propertyName: 'Downtown Apt' },
          { date: '2024-02-01', amount: 15000, bookingCount: 10, propertyId: 'prop-1', propertyName: 'Downtown Apt' },
          { date: '2024-03-01', amount: 18000, bookingCount: 12, propertyId: 'prop-1', propertyName: 'Downtown Apt' },
        ],
        growthRate: 15.5,
        status: 'active'
      },
      {
        hostId: 'host-2',
        hostName: 'Sarah Johnson',
        totalRevenue: 48000,
        propertyCount: 3,
        averageRevenuePerProperty: 16000,
        monthlyData: [
          { date: '2024-01-01', amount: 9000, bookingCount: 6, propertyId: 'prop-2', propertyName: 'Beach House' },
          { date: '2024-02-01', amount: 11000, bookingCount: 7, propertyId: 'prop-2', propertyName: 'Beach House' },
          { date: '2024-03-01', amount: 13000, bookingCount: 8, propertyId: 'prop-2', propertyName: 'Beach House' },
        ],
        growthRate: 12.3,
        status: 'active'
      },
      {
        hostId: 'host-3',
        hostName: 'Mike Wilson',
        totalRevenue: 32000,
        propertyCount: 2,
        averageRevenuePerProperty: 16000,
        monthlyData: [
          { date: '2024-01-01', amount: 6000, bookingCount: 4, propertyId: 'prop-3', propertyName: 'Mountain Cabin' },
          { date: '2024-02-01', amount: 7500, bookingCount: 5, propertyId: 'prop-3', propertyName: 'Mountain Cabin' },
          { date: '2024-03-01', amount: 8500, bookingCount: 6, propertyId: 'prop-3', propertyName: 'Mountain Cabin' },
        ],
        growthRate: 8.7,
        status: 'active'
      }
    ],
    monthlyTrends: [
      { date: '2024-01-01', amount: 45000, bookingCount: 32, propertyId: 'system', propertyName: 'System Total' },
      { date: '2024-02-01', amount: 52000, bookingCount: 38, propertyId: 'system', propertyName: 'System Total' },
      { date: '2024-03-01', amount: 58000, bookingCount: 42, propertyId: 'system', propertyName: 'System Total' },
    ],
    hostComparison: [],
    revenueDistribution: [
      { hostId: 'host-1', hostName: 'John Smith', revenue: 65000, percentage: 22.8 },
      { hostId: 'host-2', hostName: 'Sarah Johnson', revenue: 48000, percentage: 16.8 },
      { hostId: 'host-3', hostName: 'Mike Wilson', revenue: 32000, percentage: 11.2 },
      { hostId: 'others', hostName: 'Other Hosts', revenue: 140000, percentage: 49.2 },
    ]
  }), []);

  const systemData = data || mockData;

  // Filter data based on selected hosts
  const filteredHostData = useMemo(() => {
    if (selectedHosts.length === 0) {
      return systemData.topPerformingHosts;
    }
    return systemData.topPerformingHosts.filter(host => selectedHosts.includes(host.hostId));
  }, [systemData.topPerformingHosts, selectedHosts]);

  // Handle chart configuration changes
  const handleChartConfigChange = useCallback((key: keyof ChartConfig, value: any) => {
    setChartConfig(prev => ({ ...prev, [key]: value }));
  }, []);

  // Handle host selection
  const handleHostSelection = useCallback((hostIds: string[]) => {
    onHostSelectionChange?.(hostIds);
  }, [onHostSelectionChange]);

  // Format currency
  const formatCurrency = useCallback((amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, []);

  // Render loading state
  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={40} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography variant="h6">Error Loading Revenue Analytics</Typography>
        <Typography variant="body2">{error}</Typography>
        {onRefresh && (
          <Button onClick={onRefresh} sx={{ mt: 1 }}>
            Retry
          </Button>
        )}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <TrendingUpIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
              System Revenue Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Host-based revenue breakdown and time-series analysis
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Analytics Controls
        </Typography>
        
        <Grid container spacing={3} alignItems="flex-end">
          {/* View Mode */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>View Mode</InputLabel>
              <Select
                value={viewMode}
                label="View Mode"
                onChange={(e) => setViewMode(e.target.value as any)}
              >
                <MenuItem value="overview">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TimelineIcon fontSize="small" />
                    <span>Overview</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="comparison">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CompareIcon fontSize="small" />
                    <span>Host Comparison</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="trends">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <BarChartIcon fontSize="small" />
                    <span>Trends Analysis</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="distribution">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PieChartIcon fontSize="small" />
                    <span>Revenue Distribution</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Chart Type */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Chart Type</InputLabel>
              <Select
                value={chartConfig.type}
                label="Chart Type"
                onChange={(e) => handleChartConfigChange('type', e.target.value)}
              >
                <MenuItem value="line">Line</MenuItem>
                <MenuItem value="bar">Bar</MenuItem>
                <MenuItem value="area">Area</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Group By */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Group By</InputLabel>
              <Select
                value={chartConfig.groupBy}
                label="Group By"
                onChange={(e) => handleChartConfigChange('groupBy', e.target.value)}
              >
                <MenuItem value="day">Day</MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="month">Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Host Selection */}
          <Grid item xs={12} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Hosts</InputLabel>
              <Select
                multiple
                value={selectedHosts}
                label="Filter by Hosts"
                onChange={(e) => handleHostSelection(e.target.value as string[])}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((hostId) => {
                      const host = systemData.topPerformingHosts.find(h => h.hostId === hostId);
                      return (
                        <Chip
                          key={hostId}
                          label={host?.hostName || hostId}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {systemData.topPerformingHosts.map((host) => (
                  <MenuItem key={host.hostId} value={host.hostId}>
                    <Stack direction="row" justifyContent="space-between" width="100%">
                      <span>{host.hostName}</span>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(host.totalRevenue)}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* System Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TrendingUpIcon color="primary" />
                <Box>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(systemData.totalSystemRevenue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total System Revenue
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <BusinessIcon color="secondary" />
                <Box>
                  <Typography variant="h6" color="secondary">
                    {systemData.hostCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Hosts
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {formatCurrency(systemData.averageRevenuePerHost)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Revenue per Host
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main">
                {formatCurrency(systemData.averageRevenuePerProperty)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Revenue per Property
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Analytics Content */}
      {viewMode === 'overview' && (
        <Grid container spacing={3}>
          {/* System Revenue Trend */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Revenue Trends
                </Typography>
                <Box sx={{ height: 400 }}>
                  <RevenueChart
                    data={systemData.monthlyTrends}
                    chartType={chartConfig.type}
                    groupBy={chartConfig.groupBy}
                    height={350}
                    showControls={false}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Performing Hosts */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Performing Hosts
                </Typography>
                <Stack spacing={2}>
                  {systemData.topPerformingHosts.slice(0, 5).map((host, index) => (
                    <Box key={host.hostId}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            #{index + 1} {host.hostName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {host.propertyCount} properties
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body2" color="success.main">
                            {formatCurrency(host.totalRevenue)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            +{host.growthRate}%
                          </Typography>
                        </Box>
                      </Stack>
                      {index < 4 && <Divider sx={{ mt: 1 }} />}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {viewMode === 'comparison' && (
        <Grid container spacing={3}>
          {/* Host Comparison Chart */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Host Revenue Comparison
                </Typography>
                <Box sx={{ height: 400 }}>
                  {filteredHostData.length > 0 ? (
                    <RevenueChart
                      data={filteredHostData.flatMap(host => host.monthlyData)}
                      chartType={chartConfig.type}
                      groupBy={chartConfig.groupBy}
                      height={350}
                      showControls={false}
                    />
                  ) : (
                    <Alert severity="info">
                      Select hosts to compare their revenue performance.
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Host Performance Cards */}
          {filteredHostData.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Selected Host Performance
              </Typography>
              <Grid container spacing={2}>
                {filteredHostData.map((host) => (
                  <Grid item xs={12} sm={6} md={4} key={host.hostId}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {host.hostName}
                        </Typography>
                        <Stack spacing={1}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Total Revenue:
                            </Typography>
                            <Typography variant="body2" color="success.main">
                              {formatCurrency(host.totalRevenue)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Properties:
                            </Typography>
                            <Typography variant="body2">
                              {host.propertyCount}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Avg per Property:
                            </Typography>
                            <Typography variant="body2">
                              {formatCurrency(host.averageRevenuePerProperty)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Growth Rate:
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color={host.growthRate > 0 ? 'success.main' : 'error.main'}
                            >
                              {host.growthRate > 0 ? '+' : ''}{host.growthRate}%
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      )}

      {viewMode === 'trends' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Revenue Trends Analysis
                </Typography>
                <Box sx={{ height: 500 }}>
                  <RevenueChart
                    data={systemData.monthlyTrends}
                    chartType={chartConfig.type}
                    groupBy={chartConfig.groupBy}
                    height={450}
                    showControls={true}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {viewMode === 'distribution' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Revenue Distribution by Host
                </Typography>
                <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {/* Simple visual representation using progress bars */}
                  <Stack spacing={3} sx={{ width: '100%', maxWidth: 500 }}>
                    {systemData.revenueDistribution.map((item, index) => (
                      <Box key={item.hostId}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {item.hostName}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            {item.percentage}%
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            width: '100%',
                            height: 20,
                            backgroundColor: 'grey.200',
                            borderRadius: 1,
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${item.percentage}%`,
                              height: '100%',
                              backgroundColor: `hsl(${index * 60}, 70%, 50%)`,
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatCurrency(item.revenue)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Distribution Breakdown
                </Typography>
                <Stack spacing={2}>
                  {systemData.revenueDistribution.map((item) => (
                    <Box key={item.hostId}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">
                          {item.hostName}
                        </Typography>
                        <Box textAlign="right">
                          <Typography variant="body2" color="primary">
                            {item.percentage}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(item.revenue)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
});

SystemRevenueAnalytics.displayName = 'SystemRevenueAnalytics';

export default SystemRevenueAnalytics;